import { useT } from "@/lib/i18n";
import { fmtLap } from "@/lib/telemetry";

const FMT = { time: fmtLap, num: (v) => v.toFixed(1), int: (v) => Math.round(v).toString(), g: (v) => v.toFixed(2) };

function buildRows(lapA, lapB, channels, t) {
  const find = (rx) => channels.find((c) => rx.test(c));
  const sp = find(/speed/i), rpm = find(/rpm/i), gl = find(/^g[ _]?lat/i), go = find(/^g[ _]?lon/i);
  const rows = [["lap_time", t("lap_time"), "s", (l) => l.time, "time", "t"]];
  const n = Math.max(lapA.sectors?.length || 0, lapB?.sectors?.length || 0);
  for (let i = 0; i < n; i++) rows.push([`s${i}`, lapA.sector_names?.[i] || `S${i + 1}`, "s", (l) => l.sectors?.[i], "time", "t"]);
  if (lapA.theoretical != null || lapB?.theoretical != null) rows.push(["theo", t("theoretical"), "s", (l) => l.theoretical, "time", "t"]);
  if (sp) rows.push(["vmax", t("speed_max"), "km/h", (l) => l.max[sp], "num", "v"], ["vmin", t("speed_min"), "km/h", (l) => l.min[sp], "num", "v"]);
  if (rpm) rows.push(["rpmmax", t("rpm_max"), "", (l) => l.max[rpm], "int", "r"], ["rpmmin", t("rpm_min"), "", (l) => l.min[rpm], "int", "r"]);
  if (gl) rows.push(["glat", t("glat_max"), "g", (l) => Math.max(Math.abs(l.min[gl]), Math.abs(l.max[gl])), "g", "g"]);
  if (go) rows.push(["gacc", t("glon_acc"), "g", (l) => l.max[go], "g", "g"], ["gbrake", t("glon_brake"), "g", (l) => l.min[go], "g", "g"]);
  return rows;
}

const code = (name) => {
  const parts = name.trim().split(/\s+/);
  return (parts[parts.length - 1] || "").slice(0, 3).toUpperCase();
};

function shortLabel(lap) {
  if (!lap.label) return `G${lap.lap}`;
  const p = lap.label.split(" · ");
  return p.length >= 3 ? [code(p[0]), ...p.slice(1)].join(" · ") : lap.label;
}

const Diff = ({ a, b, kind }) => {
  if (a == null || b == null) return <span className="text-slate-600">—</span>;
  const d = b - a;
  const cls = kind === "time" ? (d > 0 ? "text-[#FF3B30]" : "text-[#10B981]") : "text-slate-300";
  const txt = kind === "time" ? d.toFixed(3) : FMT[kind](d);
  return <span className={cls}>{d > 0 ? "+" : ""}{txt}</span>;
};

const ColHead = ({ tag, lap, color, th }) => (
  <th className={`${th} text-right`} title={lap.label || ""}>
    <span className="inline-flex items-center gap-1.5 font-bold" style={{ color }}><span className="w-2 h-2 rounded-full" style={{ background: color }} />{tag}</span>
    <span className="block normal-case tracking-normal text-slate-400 truncate max-w-[150px] ml-auto">{shortLabel(lap)}</span>
  </th>
);

export const LapStats = ({ lapA, lapB, lapC, channels, compact = false }) => {
  const { t } = useT();
  const rows = buildRows(lapA, lapB || lapC, channels, t);
  const th = `px-3 ${compact ? "py-1" : "py-2"} font-mono text-[10px] uppercase tracking-widest align-bottom`;
  const td = `px-3 ${compact ? "py-0.5 text-[11px]" : "py-1.5 text-xs"} font-mono tabular-nums`;
  return (
    <div data-testid="lap-stats" className={`bg-[#12141C] border border-[#282D3D] rounded-sm ${compact ? "" : "overflow-x-auto"}`}>
      <table className={`w-full table-fixed ${compact ? "" : "min-w-[420px]"}`}>
        <colgroup><col className={lapC ? "w-[26%]" : "w-[34%]"} /><col />{lapB && <col />}{lapB && <col className={lapC ? "w-[13%]" : "w-[18%]"} />}{lapC && <col />}{lapC && <col className="w-[13%]" />}</colgroup>
        <thead className="border-b border-[#282D3D]">
          <tr>
            <th className={`${th} text-left text-slate-400`}>{t("stats")}</th>
            <ColHead tag="A" lap={lapA} color="#007AFF" th={th} />
            {lapB && <ColHead tag="B" lap={lapB} color="#FF3B30" th={th} />}
            {lapB && <th className={`${th} text-right text-slate-400`}>B − A</th>}
            {lapC && <ColHead tag="C" lap={lapC} color="#22D3EE" th={th} />}
            {lapC && <th className={`${th} text-right text-slate-400`}>C − A</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, label, unit, get, kind, grp], i) => {
            const a = get(lapA), b = lapB ? get(lapB) : null, c = lapC ? get(lapC) : null;
            const split = i > 0 && rows[i - 1][5] !== grp;
            return (
              <tr key={k} data-testid={`lap-stats-row-${k}`} className={split ? "border-t border-[#282D3D]" : ""}>
                <td className={`${td} text-slate-400 truncate`}>{label}{unit && <span className="ml-1.5 text-[9px] text-slate-600">({unit})</span>}</td>
                <td className={`${td} text-right text-white`}>{a != null ? FMT[kind](a) : "—"}</td>
                {lapB && <td className={`${td} text-right text-white`}>{b != null ? FMT[kind](b) : "—"}</td>}
                {lapB && <td className={`${td} text-right`}><Diff a={a} b={b} kind={kind} /></td>}
                {lapC && <td className={`${td} text-right text-white`}>{c != null ? FMT[kind](c) : "—"}</td>}
                {lapC && <td className={`${td} text-right`}><Diff a={a} b={c} kind={kind} /></td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!lapA.sectors?.length && <p data-testid="lap-stats-no-sectors" className="px-3 py-1.5 font-mono text-[10px] text-slate-500 border-t border-[#282D3D]">{t("sectors_hint")}</p>}
    </div>
  );
};
